/**
 * AgendaPartage - Orchestrateur principal
 * Gestion de l'agenda partagé avec drag & drop
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Calendar, Plus, ChevronLeft, ChevronRight, RefreshCw, Filter, Search,
  CalendarDays, LayoutGrid, List, Move, Target, TrendingUp, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { EVENT_COLORS, EVENT_TYPES, VISIBILITY_OPTIONS } from './constants';
import { safeSelectValue } from '../../lib/safeSelectValue';
import { MonthView, WeekView, DayView, ListView, UpcomingEvents } from './CalendarViews';
import { DayDetailModal, EventFormModal, MoveConfirmModal } from './EventModals';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgendaPartagé({ getAuthHeader, user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterVisibility, _setFilterVisibility] = useState('all');
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveData, setMoveData] = useState({ event: null, newDate: null });
  const [eventForm, setEventForm] = useState({
    title: '', description: '', event_type: 'meeting',
    start_datetime: '', end_datetime: '', location: '',
    is_online: false, online_link: '', visibility: 'private', reminder_minutes: 30
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setDraggedEvent(null);
    if (!over || !active) return;
    const draggedEventData = active.data.current?.event;
    const dropDate = over.data.current?.date;
    if (draggedEventData && dropDate) {
      setMoveData({ event: draggedEventData, newDate: dropDate });
      setShowMoveModal(true);
    }
  };

  const handleDragStart = (event) => {
    setDraggedEvent(event.active.data.current?.event);
  };

  const confirmMoveEvent = async () => {
    if (!moveData.event || !moveData.newDate) return;
    try {
      const oldStart = new Date(moveData.event.start_datetime);
      const oldEnd = new Date(moveData.event.end_datetime);
      const duration = oldEnd - oldStart;
      const newStart = new Date(moveData.newDate);
      newStart.setHours(oldStart.getHours(), oldStart.getMinutes(), 0, 0);
      const newEnd = new Date(newStart.getTime() + duration);
      await axios.put(`${API}/api/agenda/events/${moveData.event.id}`, {
        start_datetime: newStart.toISOString(), end_datetime: newEnd.toISOString()
      }, { headers: getAuthHeader() });
      toast.success('Événement déplacé avec succès');
      setShowMoveModal(false);
      setMoveData({ event: null, newDate: null });
      loadEvents();
    } catch {
      toast.error("Erreur lors du déplacement de l'événement");
    }
  };

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
      const params = new URLSearchParams({ start_date: startDate.toISOString(), end_date: endDate.toISOString() });
      const response = await axios.get(`${API}/api/agenda/events?${params}`, { headers: getAuthHeader() });
      setEvents(response.data.events || []);
    } catch {
      toast.error("Erreur lors du chargement de l'agenda");
    } finally {
      setLoading(false);
    }
  }, [currentDate, getAuthHeader]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = events.filter(e => {
      const d = new Date(e.start_datetime);
      return d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
    });
    const upcoming = events.filter(e => new Date(e.start_datetime) > now).slice(0, 10);
    const today = events.filter(e => new Date(e.start_datetime).toDateString() === now.toDateString()).length;
    const byType = {};
    thisMonth.forEach(e => { byType[e.event_type] = (byType[e.event_type] || 0) + 1; });
    return { total: thisMonth.length, upcoming, today, byType };
  }, [events, currentDate]);

  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      const matchesSearch = e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || e.event_type === filterType;
      const matchesVisibility = filterVisibility === 'all' || e.visibility === filterVisibility;
      return matchesSearch && matchesType && matchesVisibility;
    });
  }, [events, searchTerm, filterType, filterVisibility]);

  const handleCreateEvent = async () => {
    if (!eventForm.title || !eventForm.start_datetime || !eventForm.end_datetime) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      await axios.post(`${API}/api/agenda/events`, eventForm, { headers: getAuthHeader() });
      toast.success('Événement créé avec succès');
      setShowEventModal(false);
      resetForm();
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    try {
      await axios.put(`${API}/api/agenda/events/${selectedEvent.id}`, eventForm, { headers: getAuthHeader() });
      toast.success('Événement mis à jour');
      setShowEventModal(false);
      setSelectedEvent(null);
      resetForm();
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await axios.delete(`${API}/api/agenda/events/${eventId}`, { headers: getAuthHeader() });
      toast.success('Événement supprimé');
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEventForm({
      title: '', description: '', event_type: 'meeting',
      start_datetime: '', end_datetime: '', location: '',
      is_online: false, online_link: '', visibility: 'private', reminder_minutes: 30
    });
  };

  const openCreateModal = (date = null) => {
    resetForm();
    setSelectedEvent(null);
    const baseDate = date || new Date();
    const start = new Date(baseDate);
    start.setHours(start.getHours() + 1, 0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    setEventForm(prev => ({
      ...prev, start_datetime: start.toISOString().slice(0, 16), end_datetime: end.toISOString().slice(0, 16)
    }));
    setShowEventModal(true);
  };

  const openEditModal = (event) => {
    setSelectedEvent(event);
    const eventTypeIds = EVENT_TYPES.map(t => t.id || t.value || t);
    const visibilityIds = (VISIBILITY_OPTIONS || []).map(v => v.id || v.value || v);
    setEventForm({
      title: event.title,
      description: event.description || '',
      event_type: safeSelectValue(event.event_type, eventTypeIds, 'meeting'),
      start_datetime: event.start_datetime.slice(0, 16),
      end_datetime: event.end_datetime.slice(0, 16),
      location: event.location || '',
      is_online: event.is_online,
      online_link: event.online_link || '',
      visibility: safeSelectValue(event.visibility, visibilityIds, 'private'),
      reminder_minutes: event.reminder_minutes || 30,
    });
    setShowEventModal(true);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') newDate.setDate(newDate.getDate() + (direction * 7));
    else if (viewMode === 'month') newDate.setMonth(newDate.getMonth() + direction);
    else newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    const firstDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    for (let i = firstDayOfWeek - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), isCurrentMonth: false });
    for (let i = 1; i <= lastDay.getDate(); i++) days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    return days;
  };

  const getWeekDays = () => {
    const days = [];
    const start = new Date(currentDate);
    const dayOfWeek = start.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    start.setDate(start.getDate() + diff);
    for (let i = 0; i < 7; i++) { const d = new Date(start); d.setDate(d.getDate() + i); days.push(d); }
    return days;
  };

  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredEvents.filter(e => e.start_datetime.startsWith(dateStr));
  };

  const _getMyParticipation = (event) => {
    if (!user) return null;
    return event.participants?.find(p => p.id === user.id)?.status;
  };

  return (
    <div className="space-y-6" data-testid="agenda-partage">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-7 w-7 text-blue-600" />Agenda Partagé
          </h2>
          <p className="text-gray-500 mt-1">Gérez vos rendez-vous, réunions et événements</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-48" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36"><Filter className="h-4 w-4 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {EVENT_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />{type.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border rounded-lg overflow-hidden bg-white">
            {[
              { mode: 'day', icon: CalendarDays, label: 'Jour' },
              { mode: 'week', icon: Calendar, label: 'Sem' },
              { mode: 'month', icon: LayoutGrid, label: 'Mois' },
              { mode: 'list', icon: List, label: 'Liste' }
            ].map(({ mode, icon: Icon, label }) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 transition-colors ${
                  viewMode === mode ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`} title={label}>
                <Icon className="h-4 w-4" /><span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
          <Button variant="outline" size="icon" onClick={loadEvents}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => openCreateModal()} className="bg-blue-600 hover:bg-blue-700" data-testid="new-event-btn">
            <Plus className="h-4 w-4 mr-1" />Nouvel événement
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
                <div className="text-xs text-blue-600">Ce mois-ci</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Target className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-700">{stats.today}</div>
                <div className="text-xs text-green-600">Aujourd&apos;hui</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-700">{stats.upcoming.length}</div>
                <div className="text-xs text-purple-600">À venir</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Zap className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-700">{Object.keys(stats.byType).length}</div>
                <div className="text-xs text-orange-600">Types actifs</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => navigateDate(1)}><ChevronRight className="h-4 w-4" /></Button>
              <Button variant="ghost" onClick={() => setCurrentDate(new Date())} className="text-blue-600">Aujourd&apos;hui</Button>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 capitalize">
              {viewMode === 'day'
                ? currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                : currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </h3>
            <div className="hidden md:flex items-center gap-2">
              {Object.entries(stats.byType).slice(0, 3).map(([type, count]) => (
                <Badge key={type} style={{ backgroundColor: EVENT_COLORS[type]?.bg || '#6B7280' }} className="text-white text-xs">
                  {count} {EVENT_COLORS[type]?.label || type}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info DnD */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2">
        <Move className="h-4 w-4 text-blue-600" />
        <span className="text-sm text-blue-700">
          <strong>Glisser-déposer :</strong> Maintenez et déplacez un événement vers une autre date pour le reprogrammer
        </span>
      </div>

      {/* Vues */}
      {viewMode === 'month' && (
        <MonthView sensors={sensors} draggedEvent={draggedEvent} handleDragStart={handleDragStart}
          handleDragEnd={handleDragEnd} monthDays={getMonthDays()} getEventsForDay={getEventsForDay}
          selectedDay={selectedDay} openDayDetail={(date) => { setSelectedDay(date); setShowDayModal(true); }}
          openEditModal={openEditModal} />
      )}
      {viewMode === 'week' && (
        <WeekView weekDays={getWeekDays()} getEventsForDay={getEventsForDay} openEditModal={openEditModal} />
      )}
      {viewMode === 'day' && (
        <DayView currentDate={currentDate} dayEvents={getEventsForDay(currentDate)}
          openCreateModal={openCreateModal} openEditModal={openEditModal}
          handleDeleteEvent={handleDeleteEvent} user={user} />
      )}
      {viewMode === 'list' && (
        <ListView filteredEvents={filteredEvents} openEditModal={openEditModal}
          handleDeleteEvent={handleDeleteEvent} user={user} />
      )}
      {viewMode !== 'list' && stats.upcoming.length > 0 && (
        <UpcomingEvents upcoming={stats.upcoming} openEditModal={openEditModal} user={user} />
      )}

      {/* Modals */}
      <DayDetailModal open={showDayModal} onClose={setShowDayModal} selectedDay={selectedDay}
        getEventsForDay={getEventsForDay} openEditModal={openEditModal} openCreateModal={openCreateModal} />
      <EventFormModal open={showEventModal} onClose={setShowEventModal} selectedEvent={selectedEvent}
        eventForm={eventForm} setEventForm={setEventForm}
        onSubmit={selectedEvent ? handleUpdateEvent : handleCreateEvent} />
      <MoveConfirmModal open={showMoveModal} onClose={setShowMoveModal} moveData={moveData}
        onConfirm={confirmMoveEvent} onCancel={() => { setShowMoveModal(false); setMoveData({ event: null, newDate: null }); }} />
    </div>
  );
}
